import Button from 'components/ui/Button/Button';
import FloatingLabelInput from 'components/ui/Inputs/FloatingLabelInput';
import { usePageContext } from 'context/hooks/usePageContext';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { PAGE_SUB } from 'context/pageContext';
import useGetCardPaymentStatus from 'hooks/useGetCardPaymentStatus';
import React, { useState } from 'react';
import {
  FIRSTCHEKOUT_STATUS_PATH,
  PaymentHandlerFactory,
} from 'services/paymentHandlers';
import {
  getApiResponseMessage,
  getErrorMessage,
} from 'services/paymentHandlers/apiError';
import { checkResponse, getResponseMessage, isErrorBody } from 'utils';
import { validateOtp } from 'utils/validation';
import { PAYMENT_PROVIDER } from 'types/enums';
import { useCardContext } from 'context/hooks/useCardContext';
import { isAfrigoCard } from './helpers';

import { ReactComponent as Spinner } from '../../../assets/icons/spinner.svg';
import Icon from 'components/ui/Icon/Icon';

const OtpAuth = ({
  setUrl,
}: {
  setUrl: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { ceviantTransactionId, provider, providerTransactionRef } =
    useTransactionContext();
  const { changeSub, changePage } = usePageContext();
  const { cardBrand, otpPrompt } = useCardContext();
  const isAfrigo = isAfrigoCard(cardBrand);
  const [otpForm, setOtpForm] = useState({ otp: '' });
  const [otpErrors, setOtpErrors] = useState<Record<string, string>>({});
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  // FirstCheckout is completed and polled by its own reference, and is polled
  // until the payment settles.
  const isFirstCheckout = provider === PAYMENT_PROVIDER.FIRSTCHEKOUT;
  const reference = isFirstCheckout
    ? providerTransactionRef
    : ceviantTransactionId;

  const { triggerFetch, error } = useGetCardPaymentStatus(
    reference,
    { shouldFetch, retry: isFirstCheckout },
    isFirstCheckout ? FIRSTCHEKOUT_STATUS_PATH : `/payments/status`,
  );

  const isBusy = isOtpSubmitting || (isFirstCheckout && shouldFetch);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/\D/g, '');
    setOtpForm({ ...otpForm, [e.target.name]: sanitized });
  };

  const cardHandler = React.useMemo(
    () => PaymentHandlerFactory.card(provider as PAYMENT_PROVIDER),
    [provider],
  );

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateOtp(otpForm, { isAfrigo });
    if (validation.isValid) {
      try {
        setIsOtpSubmitting(true);
        setOtpErrors({});
        const payload = {
          otp: otpForm.otp,
          reference,
        };

        const response = await cardHandler.processOtp(payload);

        if (isErrorBody(response)) {
          changeSub(PAGE_SUB.ERROR, getResponseMessage(response));
          return;
        }

        if (isFirstCheckout) {
          setShouldFetch(true);
          return;
        }

        if (response) {
          const redirectUrl = response.redirectUrl || response.url;

          if (redirectUrl) {
            setUrl(redirectUrl);
          }

          checkResponse(
            response.data?.code || response.response,
            changeSub,
            changePage,
            getResponseMessage(response),
          );
        }
      } catch (error) {
        // A rejected FirstCheckout OTP can be retried, so keep the cardholder
        // on this screen with the reason.
        if (isFirstCheckout) {
          setOtpErrors({
            otp: getErrorMessage(
              error,
              'The OTP could not be verified, try again.',
            ),
          });
          return;
        }

        // The API rejected the request and said why (e.g. "Invalid OTP"),
        // so show that rather than polling a status that hides it.
        const apiMessage = getApiResponseMessage(error);
        if (apiMessage) {
          changeSub(PAGE_SUB.ERROR, apiMessage);
          return;
        }

        setShouldFetch(true);
        triggerFetch();
      } finally {
        setIsOtpSubmitting(false);
      }
    } else {
      setOtpErrors(validation.errors);
    }
  };

  if (error) {
    changeSub(PAGE_SUB.ERROR);
  }

  return (
    <div className="space-y-6 text-center py-14">
      <img
        src="/icons/authenticate.svg"
        alt=""
        className="m-auto w-11"
      />
      <h3 className="text-[#535353] text-lg font-semibold">
        {otpPrompt ||
          'Please input the OTP sent to your phone to continue your payment'}
      </h3>
      <form
        className="flex flex-col gap-5 px-4"
        onSubmit={handleOtpSubmit}
      >
        <FloatingLabelInput
          label="OTP"
          type="text"
          inputMode="numeric"
          value={otpForm.otp}
          onChange={handleOtpChange}
          name="otp"
        />
        {otpErrors.otp && (
          <p className="text-red-500 text-sm mt-1">{otpErrors.otp}</p>
        )}
        <div className="flex gap-3 w-full m-auto">
          <Button
            type="submit"
            variant="primary"
            disabled={isBusy}
          >
            {isBusy ? (
              <Icon
                svg={Spinner}
                size={28}
                className="mx-auto text-white"
              />
            ) : (
              `Authenticate`
            )}
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              window.parent.postMessage({ message: 'closeCeviantSDK' }, '*')
            }
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OtpAuth;
